<template>
  <div
    :class="{
      large,
      'full-width': fullWidth,
      'ws-field': $options._componentTag !== 'wds-checkbox',
      [$options._componentTag]: get($options, '_componentTag', false)
    }"
  >
    <div
      class="q-checkbox-wrapper"
      :class="{ 'full-width': fullWidth }"
    >
      <q-checkbox
        ref="qfield"
        :value="localValue"
        v-bind="$attrs"
        dense
        color="ws-primary"
        class="ws-checkbox-inner"
        :class="{ error: hasError, 'full-width': fullWidth }"
        :aria-describedby="`error-message-${_uid}`"
        v-on="$listeners"
      >
        <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
          <slot :name="slot" v-bind="scope"></slot>
        </template>
      </q-checkbox>
    </div>
    <transition name="slide-fade-vertical">
      <ws-message
        v-if="hasError"
        :id="`error-message-${_uid}`"
        type="alert"
        :messages="errors"
        @remove="innerErrorMessage = null"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import {Component, Prop} from 'vue-property-decorator';
import {mixins} from 'vue-class-component';
import {QCheckbox} from 'quasar';
import WsFieldMixin from './WsFieldMixin.vue';
import WsMessage from './WsMessage.vue';

@Component({
  components: {
    QCheckbox,
    WsMessage,
  },
})
export class WsCheckbox extends mixins(WsFieldMixin) {
  @Prop({default: false}) declare readonly value: boolean | any[];
  @Prop(Boolean) readonly forceBoolean!: boolean;
  @Prop(Boolean) readonly fullWidth!: boolean;
  @Prop(Boolean) readonly large!: boolean;

  get localValue() {
    return this.forceBoolean ? !!this.value : this.value;
  }

  set localValue(newVal: boolean | any[]) {
    if (newVal !== this.value) {
      this.$emit('input', newVal);
    }
  }

  get hasError() {
    // @ts-ignore
    return !!this.messages.length || this.innerError;
  }

  get errors() {
    // @ts-ignore
    const messages = this.inputErrorMessage;
    return messages?.length
      ? messages
      : this.errorMessage && [{text: this.errorMessage}];
  }

  onKeyPress(evt: KeyboardEvent) {
    if (evt.key && (evt.key === ' ' || evt.key === 'Enter')) {
      this.$emit('input', !this.value);
    }
  }
}

export default WsCheckbox;
</script>

<style lang="scss" scoped>
//==========>LEGACY style
.ws-checkbox {
  .q-checkbox-wrapper {
    display: inline-flex;
    align-items: normal;
    flex-wrap: wrap;

    > .ws-checkbox-inner.q-checkbox {
      position: relative;
      margin-left: 2px;

      ::v-deep {
        .q-checkbox__inner {
          align-self: baseline;
          top: 2px;
          width: 16px;
          min-width: 16px;
          height: 16px;
          min-height: 16px;

          .q-checkbox__bg {
            border-color: $ws-checkbox-border-neutral;
            background: transparent;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;

            .q-checkbox__svg {
              overflow: hidden;
              height: 70% !important;
              width: 70% !important;
              top: 15%;
              left: 15%;

              .q-checkbox__truthy {
                stroke-width: 5px;
              }
            }
          }

          &.q-checkbox__inner--truthy {
            .q-checkbox__label {
              font-weight: bold;
            }

            .q-checkbox__bg {
              border-color: $ws-success;
              background: $ws-success;
            }
          }
        }

        .q-checkbox__label {
          color: $ws-text-color-dark;
          padding-left: map-get($space-md, x);
          width: 100%;

          > small {
            overflow-wrap: anywhere;
          }
        }
      }

      &.error ::v-deep .q-checkbox__inner .q-checkbox__bg {
        border-color: $ws-red;
      }

      &.disabled {
        opacity: 1 !important; // overriding Quasar opacity

        &::v-deep .q-checkbox__inner {
          > .q-checkbox__bg {
            // styled according new design PNO-2738
            border-color: $ws-content-disabled;
          }
        }
      }

      &:not(.disabled) {
        &:hover::v-deep .q-checkbox__inner:before {
          transform: scale3d(2, 2, 1);
          opacity: 0.08;
        }

        &:active::v-deep .q-checkbox__inner:before {
          color: $ws-primary;
          opacity: 0.12;
        }

        &:active::v-deep .q-checkbox__inner--truthy:before {
          color: rgba(0, 0, 0, 0.54);
          opacity: 0.12;
        }
      }
    }
  }

  &.large .q-checkbox-wrapper {
    > .ws-checkbox-inner.q-checkbox {
      position: relative;
      margin-left: 2px;

      ::v-deep {
        .q-checkbox__inner {
          align-self: baseline;
          top: 0px;
          width: 20px;
          min-width: 20px;
          height: 20px;
          min-height: 20px;
        }
      }
    }
  }
}

//==========>new WDS style from figma
.wds-checkbox {}
</style>
